import { LocationSuggestion } from '@griply/types';
import { CircularProgress } from '@mui/material';
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { useComboboxListNavigation } from 'shared/hooks/use-combobox-list-navigation';

import * as Styled from './form-location-input.style';

export type FormLocationInputProps = {
  value: string;
  placeholder?: string;
  suggestions: LocationSuggestion[];
  isSearching?: boolean;
  // for a caller that swaps a read view for this field on click — the caret follows the click
  focusOnMount?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSelectSuggestion?: (suggestion: LocationSuggestion) => void;
};

export const FormLocationInput: React.FC<FormLocationInputProps> = ({
  value,
  placeholder,
  suggestions,
  isSearching,
  focusOnMount,
  onChange,
  onFocus,
  onBlur,
  onSelectSuggestion,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const menuOpen = isOpen && !!suggestions.length;
  const {
    activeIndex: clampedIndex,
    setActiveIndex: setHighlightedIndex,
    reset: resetHighlight,
    onArrowKeyDown,
  } = useComboboxListNavigation({
    itemCount: suggestions.length,
    isOpen: menuOpen,
  });

  const closeMenu = () => setIsOpen(false);

  const onInputChange = (next: string) => {
    onChange(next);
    setIsOpen(true);
    resetHighlight();
  };

  const selectSuggestion = (suggestion: LocationSuggestion) => {
    onSelectSuggestion?.(suggestion);
    setIsOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menuOpen) {
      return;
    }

    if (onArrowKeyDown(event)) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      selectSuggestion(suggestions[clampedIndex]);
      return;
    }

    if (event.key === 'Tab') {
      closeMenu();
    }
  };

  // mousedown on a suggestion would blur the field before its click lands, closing the menu
  const preventInputBlur = (event: React.MouseEvent) => event.preventDefault();

  useEffect(() => {
    if (!focusOnMount || !inputRef.current) {
      return;
    }
    inputRef.current.focus();
    inputRef.current.setSelectionRange(value.length, value.length);
    // eslint-disable-next-line -- once the prop changes after rendering, it is not on mount anymore
  }, []);

  return (
    <Styled.Container ref={containerRef}>
      <ResizableTextArea
        ref={inputRef}
        value={value}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        variant={ResizableTextAreaVariants.Body2}
        fullWidth
        role="combobox"
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          menuOpen ? `${listboxId}-option-${clampedIndex}` : undefined
        }
        autoComplete="off"
      />
      {!!isSearching && (
        <Styled.SpinnerContainer>
          <CircularProgress size={14} color="inherit" />
        </Styled.SpinnerContainer>
      )}
      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          onOutside={closeMenu}
          position="bottom"
          fallbackPositions={['top']}
          preventReferenceElementOverlap
          hasParentWidth
          noBackdrop
        >
          <PopupMenuList id={listboxId} role="listbox">
            {suggestions.map((suggestion, index) => {
              const handleClick = () => selectSuggestion(suggestion);
              const handleMouseEnter = () => setHighlightedIndex(index);

              return (
                <PopupMenuListItem key={suggestion.placeId} role="presentation">
                  <PopupMenuButton
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === clampedIndex}
                    isSelected={index === clampedIndex}
                    subtitle={suggestion.secondaryText || undefined}
                    onClick={handleClick}
                    onMouseDown={preventInputBlur}
                    onMouseEnter={handleMouseEnter}
                    overflowEllipsis
                  >
                    {suggestion.mainText}
                  </PopupMenuButton>
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
